import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';

type KpiCardProps = {
  label: string;
  value: string | number;
  icon?: ReactNode;
  description?: string;
  tone?: 'default' | 'danger';
  to?: string;
  linkState?: unknown;
};

export const KpiCard = ({
  label,
  value,
  icon,
  description,
  tone = 'default',
  to,
  linkState,
}: KpiCardProps) => {
  const danger = tone === 'danger';
  const card = (
    <div
      className={`rounded-xl p-6 border shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group relative overflow-hidden ${
        danger
          ? 'bg-red-50 border-red-300'
          : 'bg-gradient-to-br from-basic-white via-brand-beige-tint-1/20 to-brand-blue/5 border-brand-blue/15'
      }`}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

      <div className="relative z-10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex-1">
            <p
              className={`text-xs uppercase tracking-wider font-semibold ${
                danger ? 'text-red-700' : 'text-brand-blue/70'
              }`}
            >
              {label}
            </p>
            <p
              className={`mt-3 text-3xl font-bold ${
                danger
                  ? 'text-red-700'
                  : 'text-brand-blue bg-gradient-to-r from-brand-blue to-brand-secondary-blue bg-clip-text text-transparent'
              }`}
            >
              {value}
            </p>
          </div>
          {icon ? (
            <div
              className={`p-3 rounded-xl shadow-sm transition-colors duration-300 ${
                danger
                  ? 'text-red-700 bg-red-100'
                  : 'text-brand-blue/60 bg-brand-blue/10 group-hover:bg-brand-blue/20'
              }`}
            >
              {icon}
            </div>
          ) : null}
        </div>
        {description ? (
          <p
            className={`text-xs leading-relaxed border-t pt-3 mt-3 ${
              danger ? 'text-red-700 border-red-200' : 'text-brand-blue/60 border-brand-blue/10'
            }`}
          >
            {description}
          </p>
        ) : null}
      </div>

      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-brand-blue/50 via-brand-secondary-blue/50 to-brand-blue/50 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
    </div>
  );

  return to ? (
    <Link to={to} state={linkState} className="block">
      {card}
    </Link>
  ) : (
    card
  );
};
